import { ROUTES, EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import { ALLOWED_METHODS } from '~~/types/expondoMiddleware';
import type { FreezeCartResponse } from '~~/types/composables/frozenCart';
import { useUser } from '~/utils/ct';
import { AUTHENTICATED_QUOTE_LINK_QUERY_KEY } from '~~/constants/cartSessionTransfer';

export const useFrozenCart = () => {

  const { isCartSidebarOpen } = useCartSidebarStore();
  const { cart, isCartFrozen } = useCartExtended();
  const { getAdjustedSlug } = useRouteExtended();
  const { makeMiddlewareRequest } = useExpondoMiddleware();
  const { isAuthenticated } = useUser();

  const cartSessionTransferToken = ref<string>('');
  const frozenCartIdFromApi = ref<string>('');
  const isLoadingCartSessionTransferToken = ref(false);
  const isFreezingCart = ref(false);
  const cartIdParameterName = 'cartId';
  const cartTransferTokenParameterName = 'cst';
  const authenticatedQuoteParameterName = AUTHENTICATED_QUOTE_LINK_QUERY_KEY;

  const frozenCartIdForLink = computed(() => {

    if (frozenCartIdFromApi.value) {

      return frozenCartIdFromApi.value;

    }

    if (isCartFrozen.value && cart.value?.id) {

      return cart.value.id;

    }

    return '';

  });

  const frozenCartLink = computed(() => {

    if (!frozenCartIdForLink.value) {

      return '';

    }

    if (!isAuthenticated.value && !cartSessionTransferToken.value) {

      return '';

    }

    const origin = import.meta.client ? window.location.origin : '';
    const checkoutAddressesSlug = getAdjustedSlug(ROUTES.CHECKOUT_ADDRESSES);
    const queryParams = new URLSearchParams({
      [cartIdParameterName]: frozenCartIdForLink.value,
    });

    if (isAuthenticated.value) {

      queryParams.set(authenticatedQuoteParameterName, '1');

    } else {

      queryParams.set(cartTransferTokenParameterName, cartSessionTransferToken.value);

    }

    return `${origin}${checkoutAddressesSlug}?${queryParams.toString()}`;

  });

  const loadCartSessionTransferToken = async (cartId: string) => {

    if (!isCartSidebarOpen.value
      || !cartId
      || isLoadingCartSessionTransferToken.value) {

      return;

    }

    isLoadingCartSessionTransferToken.value = true;

    try {

      const endpoint = EXPONDO_MIDDLEWARE_ROUTES.FROZEN_CART.ROOT +
        EXPONDO_MIDDLEWARE_ROUTES.FROZEN_CART.CREATE_CART_SESSION_TRANSFER;
        
      const response = await makeMiddlewareRequest<{ transferToken?: string | null }>({
        endpoint,
        method: ALLOWED_METHODS.post,
        data: {
          cartId,
        },
      });

      cartSessionTransferToken.value = response?.transferToken || '';

    } finally {

      isLoadingCartSessionTransferToken.value = false;

    }

  };

  const handleFreezeCart = async (): Promise<boolean> => {

    if (!cart.value?.id || isFreezingCart.value) {

      return false;

    }

    isFreezingCart.value = true;

    try {

      const response = await makeMiddlewareRequest<FreezeCartResponse>({
        endpoint: EXPONDO_MIDDLEWARE_ROUTES.FROZEN_CART.ROOT + EXPONDO_MIDDLEWARE_ROUTES.FROZEN_CART.FREEZE_CART,
        method: ALLOWED_METHODS.post,
        data: {
          cartId: cart.value.id,
        },
      });

      const frozenCartId = response?.replicatedCartID
        || response?.replicatedCartId
        || response?.sourceCartID
        || response?.sourceCartId
        || '';

      if (!frozenCartId) {

        console.error('Freeze cart response did not include a replicated/source cart id:', response);

        return false;

      }

      frozenCartIdFromApi.value = frozenCartId;

      if (!isAuthenticated.value) {

        await loadCartSessionTransferToken(frozenCartId);

      }

      return Boolean(frozenCartLink.value);

    } catch (error) {

      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Freeze cart error:', errorMessage, error);
      frozenCartIdFromApi.value = '';
      cartSessionTransferToken.value = '';

      return false;

    } finally {

      isFreezingCart.value = false;

    }

  };

  const ensureFrozenCartLinkForPdf = async (): Promise<boolean> => {

    if (frozenCartLink.value) {

      return true;

    }

    if (!cart.value?.id) {

      return false;

    }

    if (!isCartFrozen.value) {

      await handleFreezeCart();

    }

    if (!isAuthenticated.value) {

      const cartIdForToken = frozenCartIdForLink.value || cart.value.id;

      if (cartIdForToken && !cartSessionTransferToken.value) {

        await loadCartSessionTransferToken(cartIdForToken);

      }

    }

    return Boolean(frozenCartLink.value);

  };

  watch([
    isCartSidebarOpen,
    isCartFrozen,
    () => cart.value?.id,
  ], async ([sidebarOpen, cartFrozen, cartId]) => {

    if (!sidebarOpen || !cartFrozen || isAuthenticated.value) {

      return;

    }

    if (!cartId) {

      return;

    }

    await loadCartSessionTransferToken(cartId);

  }, { immediate: true });

  return {
    frozenCartLink,
    isLoadingCartSessionTransferToken,
    isFreezingCart,
    handleFreezeCart,
    ensureFrozenCartLinkForPdf,
  };

};
